import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Loader2, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import { VideoPlayer } from '../interview/VideoPlayer';
import { InsightsSearch } from '../InsightsSearch';
import { ImageViewer } from '../ImageViewer';

interface Insight {
  id: string;
  title: string;
  description: string;
  start_timestamp: string;
  end_timestamp: string;
  created_at: string;
  transcription: string;
  video_clip_path: string;
  screenshot_storage_path: string | null;
  interview: {
    customer: {
      full_name: string;
    } | null;
    interview_type: 'video' | 'text';
  } | null;
}

interface InsightsSectionProps {
  projectId: string;
}

export function InsightsSection({ projectId }: InsightsSectionProps) {
  const [insights, setInsights] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedInsight, setExpandedInsight] = useState<string | null>(null);
  const [videoErrors, setVideoErrors] = useState<Record<string, boolean>>({});
  const [activeTab, setActiveTab] = useState<'list' | 'search'>('list');
  const [screenshotUrls, setScreenshotUrls] = useState<Record<string, string>>({});
  const [expandedImage, setExpandedImage] = useState<string | null>(null);

  useEffect(() => {
    fetchInsights();
  }, [projectId]);

  async function getSignedUrl(path: string): Promise<string | null> {
    try {
      const { data, error } = await supabase.storage
        .from('interview_screenshots')
        .createSignedUrl(path, 3600); // 1 hour expiry

      if (error) throw error;
      return data.signedUrl;
    } catch (error) {
      console.error('Error getting signed URL:', error);
      return null;
    }
  }

  async function fetchInsights() {
    try {
      const { data, error } = await supabase
        .from('insights')
        .select(`
          id,
          title,
          description,
          start_timestamp,
          end_timestamp,
          created_at,
          transcription,
          video_clip_path,
          screenshot_storage_path,
          interview:interviews!inner(
            project_id,
            interview_type,
            customer:customer_id(
              full_name
            )
          )
        `)
        .eq('interview.project_id', projectId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Get signed URLs for all screenshots
      const urlPromises = (data || []).map(async (insight) => {
        if (insight.screenshot_storage_path) {
          const signedUrl = await getSignedUrl(insight.screenshot_storage_path);
          return [insight.id, signedUrl];
        }
        return null;
      });

      const urls = await Promise.all(urlPromises);
      const urlMap = Object.fromEntries(urls.filter(Boolean) as [string, string][]);
      setScreenshotUrls(urlMap);
      setInsights(data || []);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }

  const toggleInsight = async (insightId: string) => {
    // If expanding and we don't have the URL yet, fetch it
    if (expandedInsight !== insightId) {
      const insight = insights.find(i => i.id === insightId);
      if (insight?.screenshot_storage_path && !screenshotUrls[insightId]) {
        const signedUrl = await getSignedUrl(insight.screenshot_storage_path);
        if (signedUrl) {
          setScreenshotUrls(prev => ({ ...prev, [insightId]: signedUrl }));
        }
      }
    }
    setExpandedInsight(expandedInsight === insightId ? null : insightId);
  };

  const handleVideoError = (insightId: string) => {
    setVideoErrors(prev => ({ ...prev, [insightId]: true }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-brand-orange" />
      </div>
    );
  }

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h2 className="text-base font-semibold leading-6 text-brand-teal">Insights</h2>
          <p className="mt-2 text-sm text-gray-700">
            Key findings and observations from your interviews
          </p>
        </div>
      </div>

      {error && (
        <div className="mt-4 rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('list')}
            className={`${
              activeTab === 'list'
                ? 'border-brand-orange text-brand-orange'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
          >
            All Insights
          </button>
          <button
            onClick={() => setActiveTab('search')}
            className={`${
              activeTab === 'search'
                ? 'border-brand-orange text-brand-orange'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
          >
            Search Insights
          </button>
        </nav>
      </div>

      <div className="mt-6">
        {activeTab === 'list' ? (
          <div className="space-y-4">
            {insights.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-sm text-gray-500">
                  No insights recorded yet. Add insights during your interviews to see them here!
                </p>
              </div>
            ) : (
              insights.map((insight) => (
                <div
                  key={insight.id}
                  className="bg-white shadow rounded-lg overflow-hidden transition-all duration-200"
                >
                  <button
                    onClick={() => toggleInsight(insight.id)}
                    className="w-full text-left px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <div>
                      <h3 className="text-lg font-medium leading-6 text-brand-teal">
                        {insight.title}
                      </h3>
                      {insight.description && (
                        <p className="mt-1 text-sm text-gray-600 line-clamp-2">
                          {insight.description}
                        </p>
                      )}
                      <div className="mt-1 flex items-center text-sm text-gray-500">
                        <span>From: {insight.interview?.customer?.full_name || 'Unknown Customer'}</span>
                        {insight.interview?.interview_type === 'video' && insight.start_timestamp && (
                          <span className="ml-4">
                            {insight.start_timestamp || '0:00'} -{' '}
                            {insight.end_timestamp || 'end'}
                          </span>
                        )}
                      </div>
                    </div>
                    {expandedInsight === insight.id ? (
                      <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                  </button>

                  {expandedInsight === insight.id && (
                    <div className="px-6 py-4 border-t border-gray-100">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {insight.interview?.interview_type === 'video' ? (
                          insight.video_clip_path && !videoErrors[insight.id] ? (
                            <div>
                              <h4 className="text-sm font-medium text-gray-700 mb-2">Video Clip</h4>
                              <VideoPlayer 
                                projectId={projectId} 
                                storagePath={insight.video_clip_path}
                                onError={() => handleVideoError(insight.id)}
                              />
                            </div>
                          ) : insight.video_clip_path ? (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center text-yellow-800">
                              <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                              <p className="text-sm">
                                The video clip is currently unavailable. This may be because it has been deleted or moved.
                              </p>
                            </div>
                          ) : null
                        ) : insight.screenshot_storage_path && screenshotUrls[insight.id] ? (
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Screenshot</h4>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedImage(screenshotUrls[insight.id]);
                              }}
                              className="block w-full cursor-zoom-in"
                            >
                              <img
                                src={screenshotUrls[insight.id]}
                                alt="Insight screenshot"
                                className="w-full h-auto rounded-lg border border-gray-200 hover:opacity-90 transition-opacity"
                              />
                            </button>
                          </div>
                        ) : insight.screenshot_storage_path ? (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center text-yellow-800">
                            <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                            <p className="text-sm">
                              The screenshot is currently unavailable. This may be because it has been deleted or moved.
                            </p>
                          </div>
                        ) : null}
                        {insight.transcription && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Transcription</h4>
                            <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700">
                              {insight.transcription}
                            </div>
                          </div>
                        )}
                      </div>
                      {insight.description && (
                        <div className="mt-4">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Description</h4>
                          <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700">
                            {insight.description}
                          </div>
                        </div>
                      )}
                      <div className="mt-4 text-xs text-gray-400">
                        Added on {new Date(insight.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        ) : (
          <InsightsSearch projectId={projectId} />
        )}
      </div>

      {expandedImage && (
        <ImageViewer
          src={expandedImage}
          onClose={() => setExpandedImage(null)}
        />
      )}
    </div>
  );
}